import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getCachedCurrencySymbol, getCachedDefaultCurrency } from '@/shared/api/global-settings';
import { formatWithToken, getActiveDateFormat } from '@/shared/i18n/date-format';

/** Compose Tailwind class strings, merging conflicting utilities. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as currency. When `currency` is omitted, falls back to
 * the tenant's Global Settings default currency (see
 * shared/api/global-settings.ts) — 'INR' until that has resolved at least
 * once in this session. When the tenant has set a custom `currency_symbol`
 * (and no explicit `currency` override is passed), the number is formatted
 * as a decimal and the custom symbol is prefixed instead of using the
 * Intl-derived glyph.
 */
export function formatCurrency(value: number, currency?: string, locale = 'en-IN'): string {
  const symbol = currency ? null : getCachedCurrencySymbol();
  if (symbol) {
    return `${symbol}${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}`;
  }
  return new Intl.NumberFormat(locale, { style: 'currency', currency: currency ?? getCachedDefaultCurrency() }).format(value);
}

/** Format a number with thousands separators. */
export function formatNumber(value: number, opts: Intl.NumberFormatOptions = {}, locale = 'en-IN'): string {
  return new Intl.NumberFormat(locale, opts).format(value);
}

/** Compact number: 1200 → "1.2K", 3450000 → "3.45M". */
export function formatCompact(value: number, locale = 'en-IN'): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/**
 * Currency sized for KPI tiles: no trailing ".00", and anything from a lakh
 * upwards is compacted (₹7.6L, ₹1.2Cr) so a big figure still fits one line.
 */
export function formatCurrencyCompact(value: number, currency?: string, locale = 'en-IN'): string {
  const cur = currency ?? getCachedDefaultCurrency();
  const symbol = currency ? null : getCachedCurrencySymbol();
  const abs = Math.abs(value);
  if (symbol) {
    const num = abs >= 100_000
      ? new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 2 }).format(value)
      : new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
    return `${symbol}${num}`;
  }
  if (abs >= 100_000) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cur,
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(value);
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: cur,
    maximumFractionDigits: 0,
  }).format(value);
}

/** Format percent from a fraction: 0.145 → "14.5%". */
export function formatPercent(value: number, digits = 1): string {
  return `${(value * 100).toFixed(digits)}%`;
}

/**
 * Format a date-ish input as a short human date. Honours the tenant's
 * `date_format` global setting via `getActiveDateFormat()` — the setting
 * is pushed into that module by `useGlobalSettingsCacheSync()` at app
 * boot, so no call site needs to thread it through. The optional
 * `locale` parameter is retained for backwards compatibility but is
 * ignored now that we format via explicit tokens (see
 * shared/i18n/date-format.ts).
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function formatDate(input: string | Date | null | undefined, _locale = 'en-IN'): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  return formatWithToken(d, getActiveDateFormat());
}

/** Format a date-ish input as a short date + time ("14 Aug 2026, 09:15"). */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function formatDateTime(input: string | Date | null | undefined, _locale = 'en-IN'): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  const datePart = formatWithToken(d, getActiveDateFormat());
  const hh = d.getHours() < 10 ? `0${d.getHours()}` : String(d.getHours());
  const mm = d.getMinutes() < 10 ? `0${d.getMinutes()}` : String(d.getMinutes());
  return `${datePart}, ${hh}:${mm}`;
}

/** Format as a relative time ("2h ago", "yesterday", "in 3 days"). */
export function formatRelative(input: string | Date, locale = 'en-IN'): string {
  const d = typeof input === 'string' ? new Date(input) : input;
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const min = 60 * 1000, hr = 60 * min, day = 24 * hr, wk = 7 * day, mo = 30 * day;
  if (abs < min)   return rtf.format(Math.round(diff / 1000), 'second');
  if (abs < hr)    return rtf.format(Math.round(diff / min),  'minute');
  if (abs < day)   return rtf.format(Math.round(diff / hr),   'hour');
  if (abs < wk)    return rtf.format(Math.round(diff / day),  'day');
  if (abs < mo)    return rtf.format(Math.round(diff / wk),   'week');
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Turn a Postgres full-name-ish string into initials for avatars. */
export function initials(name: string | null | undefined, max = 2): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, max)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}

/** Deterministic-ish hash → colour name for an avatar. */
export function avatarColor(seed: string | number): string {
  const palette = [
    'bg-violet-500', 'bg-indigo-500', 'bg-blue-500', 'bg-sky-500',
    'bg-teal-500',   'bg-emerald-500', 'bg-lime-500', 'bg-amber-500',
    'bg-orange-500', 'bg-rose-500',    'bg-pink-500', 'bg-fuchsia-500',
  ];
  const s = String(seed);
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = ((hash << 5) - hash) + s.charCodeAt(i);
  return palette[Math.abs(hash) % palette.length];
}
